import { combineReducers, configureStore } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
// Persist
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistReducer,
  persistStore,
} from 'redux-persist'
import storage from 'redux-persist/lib/storage'

// Api Slice
import { approvalApiSlice } from 'features/private-views/approval/approvalApiSlice'
import { cashFlowApiSlice } from 'features/private-views/cash-flow/cashFlowApiSlice'
import { commodityRepoApiSlice } from 'features/private-views/commodity-repo/commodityRepoApiSlice'
import { dashboardApiSlice } from 'features/private-views/dashboard/dashboardApiSlice'
import { checkFarmersApiSlice } from 'features/private-views/farmers-web/check-farmers/checkFarmerApiSlice'
import { detailFarmersApiSlice } from 'features/private-views/farmers-web/detail/detailFarmersApiSlice'
import { farmersListApiSlice } from 'features/private-views/farmers-web/farmers-list/farmersListApiSlice'
import { farmersPendingListSlice } from 'features/private-views/farmers-web/farmers-pending-list/farmersPendingSlice'
import { formFarmersApiSlice } from 'features/private-views/farmers-web/forms/formApiSlice'
import { payableNotesApiSlice } from 'features/private-views/payable-notes/payableNotesApiSlice'
import { purchaseCommodityApiSlice } from 'features/private-views/purchase-commodity/purchaseCommodityApiSlice'
import { receivableApiSlice } from 'features/private-views/receivable-notes/receivableNotesApiSlice'
import { requestTransactionApiSlice } from 'features/private-views/request-transaction/requestTransactionApiSlice'
import { sellCommodityApiSlice } from 'features/private-views/sell-commodity/sellCommodityApiSlice'
import { settingApiSlice } from 'features/private-views/settings/settingApiSlice'
import { priceTableApiSlice } from 'features/private-views/settings/PriceTable/priceTableApiSlice'
import { talanginInvoiceApiSlice } from 'features/private-views/talangin-invoice/talanginInvoiceApiSlice'
import { topupApiSlice } from 'features/private-views/topup/topupApiSlice'
import { withdrawApiSlice } from 'features/private-views/withdraw/withdrawApiSlice'
import { connectAccountApiSlice } from 'features/public-views/connect-account/connectAccountApiSlice'
import { loginApiSlice } from 'features/public-views/login/loginApiSlice'

import { formVesselsApiSlice } from 'features/private-views/vessels/forms/formApiSlice'
import { vesselsListApiSlice } from 'features/private-views/vessels/list-vessels/vesselsListApiSlice'

import { formCompaniesApiSlice } from 'features/private-views/companies/forms/formApiSlice'
import { companiesListApiSlice } from 'features/private-views/companies/list-companies/companiesListApiSlice'

import { groupListApiSlice } from 'features/private-views/farmers-web/groups-list/groupListApiSlice'
import { formFisheriesApiSlice } from 'features/private-views/fisheries/forms/formApiSlice'
import { fisheriesListApiSlice } from 'features/private-views/fisheries/list-fisheries/fisheriesListApiSlice'
import { learningReportApiSlice } from 'features/private-views/learning-report/learningReportApiSlice'
import { learningApiSlice } from 'features/private-views/learning/learningApiSlice'
import { inventoryTransferApiSlice } from 'features/private-views/transfer-inventory/transferInventoryApiSlice'
import { warehouseApiSlice } from 'features/private-views/warehouse/warehouseApiSlice'
import { loginNewApiSlice } from 'features/public-views/login-new/loginNewApiSlice'

// Reducers
import addressBookSlice from 'features/private-views/address-book/addressBookSlice'
import approvalSlice from 'features/private-views/approval/approvalSlice'
import attendanceSlice from 'features/private-views/attendance/attendanceSlice'
import cashFlowSlice from 'features/private-views/cash-flow/cashFlowSlice'
import centerAttendanceSlice from 'features/private-views/center-attendance/centerAttendanceSlice'
import dashboardReducer from 'features/private-views/dashboard/dashboardSlice'
import detailFarmersSlice from 'features/private-views/farmers-web/detail/detailSlice'
import farmersListSlice from 'features/private-views/farmers-web/farmers-list/farmersListSlice'
import formFarmersSlice from 'features/private-views/farmers-web/forms/formSlice'
import groupsListSlice from 'features/private-views/farmers-web/groups-list/groupsListSlice'
import staffListSlice from 'features/private-views/farmers-web/staffList/staffListSlice'
import learningReportSlice from 'features/private-views/learning-report/learningReportSlice'
import payableNotesSlice from 'features/private-views/payable-notes/payableNotesSlice'
import purchaseCommodityReducer from 'features/private-views/purchase-commodity/purchaseCommoditySlice'
import receivableNotesSlice from 'features/private-views/receivable-notes/receivableNotesSlice'
import sellCommodityReducer from 'features/private-views/sell-commodity/sellCommoditySlice'
import settingSlice from 'features/private-views/settings/settingSlice'
import priceTableSlice from 'features/private-views/settings/PriceTable/priceTableSlice'
import talanginInvoiceSlice from 'features/private-views/talangin-invoice/talanginInvoiceSlice'
import talanginTable from 'features/private-views/talangin-invoice/talanginSlice'
import tncSlice from 'features/private-views/tnc/tncSlice'
import topupSlice from 'features/private-views/topup/topupSlice'
import inventoryTransferSlice from 'features/private-views/transfer-inventory/transferInventorySlice'
import warehouseSlice from 'features/private-views/warehouse/warehouseSlice'
import withdrawSlice from 'features/private-views/withdraw/withdrawSlice'
import publicVersionReducer from 'features/public-api/publicVersion/publicVersionSlice'
import connectAccountSlice from 'features/public-views/connect-account/connectAccountSlice'
import inventoryMembersSlice from 'features/public-views/inventoryMembers/API/inventoryMembersSlice'
import loginReducer from 'features/public-views/login/loginSlice'
import manageStockSlice from 'features/public-views/manageStock/API/manageStockSlice'
import requestTransactionReducer from 'features/public-views/order/requestTransactionSlice'
import commodityPurchaseSlice from 'private-views/transactions/commodityPurchaseSlice'

// VESSELS
import detailVesselsSlice from 'features/private-views/vessels/detail/detailSlice'
import formVesselsSlice from 'features/private-views/vessels/forms/formSlice'
import vesselsListSlice from 'features/private-views/vessels/list-vessels/vesselsListSlice'

// CATCHES
import detailCatchesSlice from 'features/private-views/catches/detail/detailSlice'
import { catchesListApiSlice } from 'features/private-views/catches/list-catches/catchesListApiSlice'
import catchesListSlice from 'features/private-views/catches/list-catches/catchesListSlice'

// COMPANIES
import formCompaniesSlice from 'features/private-views/companies/forms/formSlice'
import companiesListSlice from 'features/private-views/companies/list-companies/companiesListSlice'

// FISHERIES
import formFisheriesSlice from 'features/private-views/fisheries/forms/formSlice'
import fisheriesListSlice from 'features/private-views/fisheries/list-fisheries/fisheriesListSlice'

// WHISTLE BLOWINGS
import { whistleBlowingsListApiSlice } from 'features/private-views/whistle-blowings/list-whistle-blowings/whistleBlowingsListApiSlice'
import whistleBlowingsListSlice from 'features/private-views/whistle-blowings/list-whistle-blowings/whistleBlowingsListSlice'

// ANALYTICS REPORT
import insightListSlice from 'features/public-views/ap2hi/insight/insightListSlice'
import { registerApiSlice } from 'features/public-views/register-universal/registerSlice'

// CONFIGURATIONS
import { configurationsListApiSlice } from 'features/private-views/configurations/list-configurations/configurationsListApiSlice'
import configurationsListSlice from 'features/private-views/configurations/list-configurations/configurationsListSlice'

// REPORT
import { reportPnbpListApiSlice } from 'features/private-views/report-pnbp/list-report-pnbp/reportPnbpListApiSlice'
import reportPnbpListSlice from 'features/private-views/report-pnbp/list-report-pnbp/reportPnbpListSlice'

import {
  publicApiDevSlice,
  publicApiSlice,
} from 'features/public-api/publicVersion/publicApiSlice'

//ATTENDANCE SUMMARY
import { reportAttendanceApiSlice } from 'features/private-views/attendance-report/reportAttendanceApiSlice'
import reportAttendanceSlice from 'features/private-views/attendance-report/reportAttendanceSlice'
import { attendanceSummaryApiSlice } from 'features/private-views/attendance-summary/attendanceSummaryApiSlice'
import attendanceSummarySlice from 'features/private-views/attendance-summary/attendanceSummarySlice'

//notifinbox
import { notifInboxApiSlice } from 'features/private-views/notification-inbox/notifInboxApiSlice'
import notifInboxSlice from 'features/private-views/notification-inbox/notifInboxSlice'
import reportFulfilmentSlice from 'features/private-views/report-fulfilment/reportFulfilmentSlice'

//SYNC DATA
import { syncDataListApiSlice } from 'features/private-views/sync-data/syncDataListApiSlice'
import syncDataListSlice from 'features/private-views/sync-data/syncDataListSlice'

//PRODUCT MAPPING
import { productMappingListApiSlice } from 'features/private-views/product-mapping/productMappingListApiSlice'
import productMappingListSlice from 'features/private-views/product-mapping/productMappingListSlice'

//PRODUCT MAPPING SCHEMA
import { productMappingSchemaListApiSlice } from 'features/private-views/product-mapping-schema/productMappingSchemaListApiSlice'
import productMappingSchemaListSlice from 'features/private-views/product-mapping-schema/productMappingSchemaListSlice'

//WORKPLACE
import workplaceListSlice from 'features/private-views/workplace/workplaceListSlice'

//FINDORA LOGS
import { findoraLogsListApiSlice } from 'features/private-views/findora-logs/findoraLogsListApiSlice'
import findoraLogsListSlice from 'features/private-views/findora-logs/findoraLogsListSlice'

//PRODUCTION UNIT
import { productionUnitApiSlice } from 'features/private-views/settings/production-unit/productionUnitApiSlice'
import productionUnitSlice from 'features/private-views/settings/production-unit/productionUnitSlice'

//PROJECT
import { projectApiSlice } from 'features/private-views/project/projectApiSlice'
import projectSlice from 'features/private-views/project/projectSlice'

//FINDORA DASHBOARD
import { findoraDashboardListApiSlice } from 'features/private-views/findora-dashboard/findoraDashboardListApiSlice'
import findoraDashboardListSlice from 'features/private-views/findora-dashboard/findoraDashboardListSlice'

//HOMEPAGE
import { homepageApiSlice } from 'features/private-views/homepage/homepageApiSlice'
import homepageSlice from 'features/private-views/homepage/homepageSlice'

//ANNUAL CAPACITY
import { annualCapacityApiSlice } from 'features/private-views/annual-capacity/annualCapacityApiSlice'
import annualCapacitySlice from 'features/private-views/annual-capacity/annualCapacitySlice'

//PROJECT SUMMARY DASHBOARD
import { projectDashboardListApiSlice } from 'features/private-views/project-dashboard/projectDashboardListApiSlice'
import projectDashboardListSlice from 'features/private-views/project-dashboard/projectDashboardListSlice'

//EMPLOYEE PAYMENTS
import { employeePaymentsListApiSlice } from 'features/private-views/employee-payments/employeePaymentsListApiSlice'
import employeePaymentsListSlice from 'features/private-views/employee-payments/employeePaymentsListSlice'

//EVENT PARTNER
import { eventPartnerApiSlice } from 'features/private-views/event-partner/eventPartnerApiSlice'
import eventPartnerSlice from 'features/private-views/event-partner/eventPartnerSlice'

//EVENT
import { eventApiSlice } from 'features/private-views/event/eventApiSlice'
import eventSlice from 'features/private-views/event/eventSlice'

//EVENT PARTICIPANT
import { eventParticipantApiSlice } from 'features/private-views/event-participant/eventParticipantApiSlice'
import eventParticipantSlice from 'features/private-views/event-participant/eventParticipantSlice'
//PROJECTS MENU
import { projectsMenuListApiSlice } from 'features/private-views/projects-menu/projectsMenuListApiSlice'
import projectsMenuListSlice from 'features/private-views/projects-menu/projectsMenuListSlice'
//MASTER ITEMS
import { masterItemsApiSlice } from 'features/private-views/master-items/masterItemsApiSlice'
import masterItemsSlice from 'features/private-views/master-items/masterItemsSlice'

//MAP SCHEMA
import { mapSchemaApiSlice } from 'features/private-views/map-schema/mapSchemaApiSlice'
import mapSchemaSlice from 'features/private-views/map-schema/mapSchemaSlice'

//PROCESS
import { processApiSlice } from 'features/private-views/process/processApiSlice'
import processSlice from 'features/private-views/process/processSlice'
//EVENT APPROVAL
import { eventApprovalApiSlice } from 'features/private-views/event/event-approval/eventApprovalApiSlice'
import eventApprovalSlice from 'features/private-views/event/event-approval/eventApprovalSlice'

//INVENTORI INTELLIGENT
import { inventoriIntelligentApiSlice } from 'features/private-views/inventori-intelligent/inventoriIntelligentApiSlice'
import inventoriIntelligentSlice from 'features/private-views/inventori-intelligent/inventoriIntelligentSlice'

//SCANNER
import scannerReducer from 'features/private-views/scanner/scannerSlice'

//DASHBOARD TRACEABILITY
import { dashboardTraceabilityApiSlice } from 'features/private-views/dashboard-traceability/dashboardTraceabilityApiSlice'
import dashboardTraceabilitySlice from 'features/private-views/dashboard-traceability/dashboardTraceabilitySlice'

const persistConfig = {
  key: 'root',
  version: 1,
  storage,
  whitelist: [
    'auth',
    'dashboard',
    'cashflow',
    'catchesList',
    'whistleBlowingsList',
    'insightList',
    'publicVersion',
    'workplaceList',
    'findoraLogsList',
    'employeePaymentsList',
    'inventoriIntelligent',
  ],
}

const combinedReducer = combineReducers({
  auth: loginReducer,
  publicVersion: publicVersionReducer,
  [loginApiSlice.reducerPath]: loginApiSlice.reducer,
  dashboard: dashboardReducer,
  [dashboardApiSlice.reducerPath]: dashboardApiSlice.reducer,
  withdraw: withdrawSlice,
  [withdrawApiSlice.reducerPath]: withdrawApiSlice.reducer,
  talanginTable: talanginTable,
  setting: settingSlice,
  topup: topupSlice,
  farmersList: farmersListSlice,
  [farmersListApiSlice.reducerPath]: farmersListApiSlice.reducer,
  farmersDetail: detailFarmersSlice,
  farmersForm: formFarmersSlice,
  [formFarmersApiSlice.reducerPath]: formFarmersApiSlice.reducer,
  [checkFarmersApiSlice.reducerPath]: checkFarmersApiSlice.reducer,
  farmersPending: farmersPendingListSlice,
  cashflow: cashFlowSlice,
  [detailFarmersApiSlice.reducerPath]: detailFarmersApiSlice.reducer,
  groupsList: groupsListSlice,
  staffList: staffListSlice,
  [groupListApiSlice.reducerPath]: groupListApiSlice.reducer,

  // VESSELS
  vesselsList: vesselsListSlice,
  [vesselsListApiSlice.reducerPath]: vesselsListApiSlice.reducer,
  vesselsForm: formVesselsSlice,
  [formVesselsApiSlice.reducerPath]: formVesselsApiSlice.reducer,
  vesselsDetail: detailVesselsSlice,

  // CATCHES
  catchesList: catchesListSlice,
  [catchesListApiSlice.reducerPath]: catchesListApiSlice.reducer,
  catchesDetail: detailCatchesSlice,

  // COMPANIES
  companiesList: companiesListSlice,
  [companiesListApiSlice.reducerPath]: companiesListApiSlice.reducer,
  companiesForm: formCompaniesSlice,
  [formCompaniesApiSlice.reducerPath]: formCompaniesApiSlice.reducer,

  // FISHERIES
  fisheriesList: fisheriesListSlice,
  [fisheriesListApiSlice.reducerPath]: fisheriesListApiSlice.reducer,
  fisheriesForm: formFisheriesSlice,
  [formFisheriesApiSlice.reducerPath]: formFisheriesApiSlice.reducer,

  // WHISTLE BLOWINGS
  whistleBlowingsList: whistleBlowingsListSlice,
  [whistleBlowingsListApiSlice.reducerPath]:
    whistleBlowingsListApiSlice.reducer,

  // ANALITYCS REPORT
  insightList: insightListSlice,

  // CONFIGURATIONS
  configurationsList: configurationsListSlice,
  [configurationsListApiSlice.reducerPath]: configurationsListApiSlice.reducer,

  // REPORT
  reportPnbp: reportPnbpListSlice,
  [reportPnbpListApiSlice.reducerPath]: reportPnbpListApiSlice.reducer,

  //ATTENDANCE SUMMARY
  attendanceSummary: attendanceSummarySlice,
  [attendanceSummaryApiSlice.reducerPath]: attendanceSummaryApiSlice.reducer,
  reportAttendance: reportAttendanceSlice,
  [reportAttendanceApiSlice.reducerPath]: reportAttendanceApiSlice.reducer,

  //notif + inbox
  notifInbox: notifInboxSlice,
  [notifInboxApiSlice.reducerPath]: notifInboxApiSlice.reducer,

  // REPORT X
  reportFulfilment: reportFulfilmentSlice,

  //WORKPLACE
  workplaceList: workplaceListSlice,

  //SYNC DATA
  syncDataList: syncDataListSlice,
  [syncDataListApiSlice.reducerPath]: syncDataListApiSlice.reducer,

  //PRODUCT MAPPING
  productMappingList: productMappingListSlice,
  [productMappingListApiSlice.reducerPath]: productMappingListApiSlice.reducer,

  //PRODUCT MAPPING SCHEMA
  productMappingSchemaList: productMappingSchemaListSlice,
  [productMappingSchemaListApiSlice.reducerPath]:
    productMappingSchemaListApiSlice.reducer,

  //FINDORA LOGS
  findoraLogsList: findoraLogsListSlice,
  [findoraLogsListApiSlice.reducerPath]: findoraLogsListApiSlice.reducer,

  //PRODUCTION UNIT
  productionUnit: productionUnitSlice,
  [productionUnitApiSlice.reducerPath]: productionUnitApiSlice.reducer,

  //PROJECT
  project: projectSlice,
  [projectApiSlice.reducerPath]: projectApiSlice.reducer,

  //FINDORA DASHBOARD
  findoraDashboardList: findoraDashboardListSlice,
  [findoraDashboardListApiSlice.reducerPath]:
    findoraDashboardListApiSlice.reducer,

  //HOMEPAGE
  homepage: homepageSlice,
  [homepageApiSlice.reducerPath]: homepageApiSlice.reducer,

  //ANNUAL CAPACITY
  annualCapacity: annualCapacitySlice,
  [annualCapacityApiSlice.reducerPath]: annualCapacityApiSlice.reducer,

  //PROJECT DASHBOARD
  projectDashboardList: projectDashboardListSlice,
  [projectDashboardListApiSlice.reducerPath]:
    projectDashboardListApiSlice.reducer,

  //EMPLOYEE PAYMENTS
  employeePaymentsList: employeePaymentsListSlice,
  [employeePaymentsListApiSlice.reducerPath]:
    employeePaymentsListApiSlice.reducer,

  //EVENT PARTNER
  eventPartner: eventPartnerSlice,
  [eventPartnerApiSlice.reducerPath]: eventPartnerApiSlice.reducer,

  //EVENT
  event: eventSlice,
  [eventApiSlice.reducerPath]: eventApiSlice.reducer,
  eventApproval: eventApprovalSlice,
  [eventApprovalApiSlice.reducerPath]: eventApprovalApiSlice.reducer,

  //EVENT PARTICIPANT
  eventParticipant: eventParticipantSlice,
  [eventParticipantApiSlice.reducerPath]: eventParticipantApiSlice.reducer,
  //PROJECTS MENU
  projectsMenuList: projectsMenuListSlice,
  [projectsMenuListApiSlice.reducerPath]: projectsMenuListApiSlice.reducer,
  //MASTER ITEMS
  masterItems: masterItemsSlice,
  [masterItemsApiSlice.reducerPath]: masterItemsApiSlice.reducer,

  //MAP SCHEMA
  mapSchema: mapSchemaSlice,
  [mapSchemaApiSlice.reducerPath]: mapSchemaApiSlice.reducer,

  //PROCESS
  process: processSlice,
  [processApiSlice.reducerPath]: processApiSlice.reducer,

  //INVENTORI INTELLIGENT
  inventoriIntelligent: inventoriIntelligentSlice,
  [inventoriIntelligentApiSlice.reducerPath]:
    inventoriIntelligentApiSlice.reducer,

  //DASHBOARD TRACEABILITY
  [dashboardTraceabilityApiSlice.reducerPath]:
    dashboardTraceabilityApiSlice.reducer,
  dashboardTraceability: dashboardTraceabilitySlice,

  purchaseCommmodity: purchaseCommodityReducer,
  approval: approvalSlice,
  talanginInvoice: talanginInvoiceSlice,
  sellCommodity: sellCommodityReducer,
  reqTrxSell: requestTransactionReducer,
  comodityPurchase: commodityPurchaseSlice,
  payableNotes: payableNotesSlice,
  receivableNotes: receivableNotesSlice,
  attendande: attendanceSlice,
  centerAttendance: centerAttendanceSlice,
  [topupApiSlice.reducerPath]: topupApiSlice.reducer,
  [settingApiSlice.reducerPath]: settingApiSlice.reducer,
  [talanginInvoiceApiSlice.reducerPath]: talanginInvoiceApiSlice.reducer,
  [sellCommodityApiSlice.reducerPath]: sellCommodityApiSlice.reducer,
  [purchaseCommodityApiSlice.reducerPath]: purchaseCommodityApiSlice.reducer,
  [publicApiDevSlice.reducerPath]: publicApiDevSlice.reducer,
  [publicApiSlice.reducerPath]: publicApiSlice.reducer,
  [approvalApiSlice.reducerPath]: approvalApiSlice.reducer,
  [requestTransactionApiSlice.reducerPath]: requestTransactionApiSlice.reducer,
  [commodityRepoApiSlice.reducerPath]: commodityRepoApiSlice.reducer,
  [cashFlowApiSlice.reducerPath]: cashFlowApiSlice.reducer,
  [payableNotesApiSlice.reducerPath]: payableNotesApiSlice.reducer,
  [receivableApiSlice.reducerPath]: receivableApiSlice.reducer,
  connectAccount: connectAccountSlice,
  [connectAccountApiSlice.reducerPath]: connectAccountApiSlice.reducer,
  tnc: tncSlice,
  [loginNewApiSlice.reducerPath]: loginNewApiSlice.reducer,
  [registerApiSlice.reducerPath]: registerApiSlice.reducer,
  [learningApiSlice.reducerPath]: learningApiSlice.reducer,
  [warehouseApiSlice.reducerPath]: warehouseApiSlice.reducer,
  warehouse: warehouseSlice,
  [inventoryTransferApiSlice.reducerPath]: inventoryTransferApiSlice.reducer,
  transferInventory: inventoryTransferSlice,
  manageStock: manageStockSlice,
  learningReport: learningReportSlice,
  [learningReportApiSlice.reducerPath]: learningReportApiSlice.reducer,
  inventoryMembers: inventoryMembersSlice,
  addressBook: addressBookSlice,

  // PRICE TABLE
  priceTable: priceTableSlice,
  [priceTableApiSlice.reducerPath]: priceTableApiSlice.reducer,

  // SCANNER
  scanner: scannerReducer,
})

const rootReducer = (state, action) => {
  if (action.type === 'auth/logout') {
    state = undefined
  }
  return combinedReducer(state, action)
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

export const makeStore = (preloadedState) => {
  const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) => {
      return getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).concat(
        topupApiSlice.middleware,
        settingApiSlice.middleware,
        purchaseCommodityApiSlice.middleware,
        publicApiDevSlice.middleware,
        publicApiSlice.middleware,
        approvalApiSlice.middleware,
        withdrawApiSlice.middleware,
        talanginInvoiceApiSlice.middleware,
        sellCommodityApiSlice.middleware,
        requestTransactionApiSlice.middleware,
        dashboardApiSlice.middleware,
        loginApiSlice.middleware,
        farmersListApiSlice.middleware,
        formFarmersApiSlice.middleware,
        checkFarmersApiSlice.middleware,
        commodityRepoApiSlice.middleware,
        cashFlowApiSlice.middleware,
        payableNotesApiSlice.middleware,
        receivableApiSlice.middleware,
        detailFarmersApiSlice.middleware,
        groupListApiSlice.middleware,
        vesselsListApiSlice.middleware,
        formVesselsApiSlice.middleware,
        loginNewApiSlice.middleware,
        registerApiSlice.middleware,
        catchesListApiSlice.middleware,
        companiesListApiSlice.middleware,
        formCompaniesApiSlice.middleware,
        fisheriesListApiSlice.middleware,
        formFisheriesApiSlice.middleware,
        whistleBlowingsListApiSlice.middleware,
        learningApiSlice.middleware,
        warehouseApiSlice.middleware,
        inventoryTransferApiSlice.middleware,
        learningReportApiSlice.middleware,
        configurationsListApiSlice.middleware,
        reportPnbpListApiSlice.middleware,
        reportAttendanceApiSlice.middleware,
        attendanceSummaryApiSlice.middleware,
        syncDataListApiSlice.middleware,
        productMappingListApiSlice.middleware,
        productMappingSchemaListApiSlice.middleware,
        findoraLogsListApiSlice.middleware,
        productionUnitApiSlice.middleware,
        projectApiSlice.middleware,
        findoraDashboardListApiSlice.middleware,
        homepageApiSlice.middleware,
        annualCapacityApiSlice.middleware,
        projectDashboardListApiSlice.middleware,
        employeePaymentsListApiSlice.middleware,
        eventPartnerApiSlice.middleware,
        eventApiSlice.middleware,
        eventParticipantApiSlice.middleware,
        projectsMenuListApiSlice.middleware,
        masterItemsApiSlice.middleware,
        mapSchemaApiSlice.middleware,
        processApiSlice.middleware,
        inventoriIntelligentApiSlice.middleware,
        priceTableApiSlice.middleware,
      )
    },
    preloadedState,
  })
  setupListeners(store.dispatch)
  return store
}

export const store = makeStore()
export let persistor = persistStore(store)
